/**
 * `OsLida` — o que sai do leitor de PDF, antes de virar OS no banco.
 *
 * É o contrato entre três lados que não se conhecem: o leitor (navegador ou
 * agente), a função de importação do servidor e os testes de contrato das 21
 * OS de amostra. Por isso ele é validado com Zod nos três, a partir daqui.
 *
 * O formato espelha `docs/ANATOMIA_DA_OS.md` campo a campo. Um campo que a OS
 * não traz é `null`, nunca ausente — assim a comparação com as fixtures não
 * depende de `undefined` versus chave faltando.
 */

import { z } from 'zod';

export const zEmissao = z.object({
  /** "31/08/26" — como impresso, ano de dois dígitos. */
  data: z.string(),
  hora: z.string(),
  diaSemana: z.string(),
  usuario: z.string(),
});

export const zAlteracao = z.object({
  data: z.string(),
  hora: z.string(),
  usuario: z.string(),
});

export const zPrevisao = z.object({
  /** "04/09/2026" — ano de quatro dígitos, diferente da emissão. */
  data: z.string(),
  hora: z.string(),
  diaSemana: z.string(),
});

export const zContato = z.object({
  nome: z.string().nullable(),
  telefone: z.string().nullable(),
  email: z.string().nullable(),
});

export const zOrcamento = z.object({
  numero: z.string(),
  item: z.string(),
  vendedor: z.string(),
});

export const zCabecalho = z.object({
  clienteCurto: z.string(),
  pagina: z.number().int().positive(),
  paginas: z.number().int().positive(),
  osNumero: z.number().int().positive(),
  emissao: zEmissao,
  alteracao: zAlteracao.nullable(),
  previsaoEntrega: zPrevisao.nullable(),
  quantidade: z.number().int().nonnegative(),
  produto: z.string(),
  /** "OS CONCLUÍDA" quando o carimbo veio impresso. */
  carimbo: z.string().nullable(),
  clienteCodigo: z.string().nullable(),
  clienteNome: z.string().nullable(),
  contato: zContato.nullable(),
  orcamento: zOrcamento.nullable(),
  transportadora: z.string().nullable(),
  observacoesGerais: z.array(z.string()),
});

export const zPlano = z.object({
  numero: z.number().int().positive(),
  tipo: z.enum(['cores', 'pags_cores_lams']),
  cores: z.string().nullable(),
  paginas: z.number().int().nullable(),
  laminas: z.string().nullable(),
  formatoFinal: z.string().nullable(),
  formatoFinalUnidade: z.enum(['cm', 'mm']).nullable(),
  pecasPorFolha: z.number().int().nullable(),
  materialCodigo: z.string().nullable(),
  materialFormato: z.string().nullable(),
  montagem: z.string().nullable(),
  tiraRetira: z.boolean(),
  impressora: z.string().nullable(),
  abertura: z.string().nullable(),
  refileSangria: z.string().nullable(),
  formatoImpressao: z.string().nullable(),
  faca: z.string().nullable(),
  lombada: z.string().nullable(),
  solda: z.boolean(),
  outrosDetalhes: z.string().nullable(),
});

export const zEtapa = z.object({
    secao: z.string(),
  sequencia: z.number().int().nonnegative(),
  nome: z.string(),
  plano: z.number().int().nullable(),
  equipamento: z.string().nullable(),
  impressoes: z.number().nullable(),
  impressoesUnidade: z.string().nullable(),
  /** "1:02" como impresso. */
  tempoPrevisto: z.string().nullable(),
  tempoPrevistoSeg: z.number().int().nullable(),
  observacoes: z.string().nullable(),
});

export const zEspecificacoes = z.object({
  entrega: z.string().nullable(),
  nota: z.string().nullable(),
  ultimaOs: z.string().nullable(),
  arquivar: z.string().nullable(),
  ordemCompraCliente: z.string().nullable(),
  acabamentoAdesivoVinil: z.string().nullable(),
  /** Chaves que o leitor não conhecia. Vira aviso, não quarentena. */
  outros: z.record(z.string(), z.string()),
});

export const zMaterial = z.object({
  codigo: z.string().nullable(),
  nome: z.string(),
  plano: z.number().int().nullable(),
  quantidade: z.number(),
  unidade: z.string(),
  quebraPct: z.number().nullable(),
  observacoes: z.string().nullable(),
});

export const zOsLida = z.object({
  cabecalho: zCabecalho,
  planos: z.array(zPlano),
  etapas: z.array(zEtapa),
  especificacoes: zEspecificacoes,
  materiais: z.array(zMaterial),
  /** Linhas brutas da tabela de entregas, quando a OS traz uma. */
  entregas: z.array(z.string()),
  avisos: z.array(z.string()),
  tempoPrevistoTotalSeg: z.number().int().nonnegative(),
  /** SHA-256 do texto normalizado. Chave da versão da OS. */
  hashTexto: z.string(),
  versaoLeitor: z.string(),
  /** A imagem da faca, quando o PDF traz uma. */
  temImagem: z.boolean(),
});

export type CabecalhoLido = z.infer<typeof zCabecalho>;
export type PlanoLido = z.infer<typeof zPlano>;
export type EtapaLida = z.infer<typeof zEtapa>;
export type EspecificacoesLidas = z.infer<typeof zEspecificacoes>;
export type MaterialLido = z.infer<typeof zMaterial>;
export type OsLida = z.infer<typeof zOsLida>;

/**
 * O que é obrigatório para a OS entrar (ANATOMIA §4).
 *
 * Falta de qualquer um manda o PDF para a quarentena, com o PDF visível e o
 * motivo em português. Nunca importa pela metade: uma OS sem etapas entraria
 * na carteira parecendo pronta, e ninguém descobriria até o material sumir.
 */
export function motivoDeQuarentena(os: OsLida): string | null {
  const c = os.cabecalho;
  if (!c.osNumero) return 'Não achei o número da OS.';
  if (!c.emissao?.data) return 'Não achei a data de emissão.';
  if (!c.previsaoEntrega?.data) return 'Não achei a previsão de entrega.';
  if (!c.produto) return 'Não achei a descrição do produto.';
  if (!c.clienteNome && !c.clienteCurto) return 'Não achei o cliente.';
  if (os.planos.length === 0) return 'A OS não tem nenhum plano.';
  if (os.etapas.length === 0) return 'A OS não tem nenhuma etapa.';
  return null;
}

/** Converte "31/08/26" + "14:07" no instante ISO da gráfica (UTC−03:00). */
export function instanteDaOs(dataBr: string, hhmmTexto: string): string | null {
  const m = /^(\d{2})\/(\d{2})\/(\d{2}|\d{4})$/.exec(dataBr.trim());
  if (!m) return null;
  const dia = Number(m[1]);
  const mes = Number(m[2]);
  const anoTexto = m[3] ?? '';
  const ano = anoTexto.length === 2 ? 2000 + Number(anoTexto) : Number(anoTexto);
  const hm = /^(\d{1,2}):(\d{2})$/.exec(hhmmTexto.trim());
  const hora = hm ? Number(hm[1]) : 0;
  const minuto = hm ? Number(hm[2]) : 0;
  if (!Number.isFinite(dia) || !Number.isFinite(mes) || !Number.isFinite(ano)) return null;
  // −03:00 fixo: o Brasil não tem horário de verão desde 2019.
  const utc = Date.UTC(ano, mes - 1, dia, hora, minuto) + 3 * 3600_000;
  return new Date(utc).toISOString();
}
